import React, { useState } from 'react';
import { Gauge, Loader2, Play } from 'lucide-react';
import {
    eseguiTestConnessione, testoRisultato, testoStime,
    FaseTest, RisultatoConnessione, TestConnessioneError,
} from '../utils/connessioneTest';

const ETICHETTA_FASE: Record<FaseTest, string> = {
    latenza: 'Misuro la latenza…',
    download: 'Misuro la velocità di download…',
    upload: 'Misuro la velocità di upload…',
};

/** Test della connessione verso il server dell'agenzia (Impostazioni, solo titolare). */
const ConnessioneTestPanel: React.FC = () => {
    const [inCorso, setInCorso] = useState(false);
    const [fase, setFase] = useState<FaseTest>('latenza');
    const [avanzamento, setAvanzamento] = useState(0);
    const [risultato, setRisultato] = useState<RisultatoConnessione | null>(null);
    const [errore, setErrore] = useState<string | null>(null);

    const avvia = async () => {
        setInCorso(true);
        setErrore(null);
        setRisultato(null);
        setAvanzamento(0);
        try {
            const r = await eseguiTestConnessione((f, frazione) => {
                setFase(f);
                setAvanzamento(frazione);
            });
            setAvanzamento(1);
            setRisultato(r);
        } catch (e) {
            setErrore(e instanceof TestConnessioneError ? e.message : 'Test non riuscito. Riprova.');
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b pb-3">
                <h3 className="font-bold text-gray-900 flex items-center gap-2 text-lg">
                    <Gauge size={20} className="text-blue-600"/> Test della connessione
                </h3>
                <button
                    onClick={avvia}
                    disabled={inCorso}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-blue-700 disabled:opacity-60 shadow-sm transition-colors"
                >
                    {inCorso ? <Loader2 size={16} className="animate-spin"/> : <Play size={16}/>}
                    {inCorso ? 'Test in corso…' : 'Avvia test'}
                </button>
            </div>

            <p className="text-sm text-gray-600">
                Misura in circa 15–20 secondi la velocità fra questo PC e il server dell'agenzia,
                per capire quanto tempo serve ad aprire relazioni e documenti pesanti.
            </p>

            {inCorso && (
                <div className="space-y-2" aria-live="polite">
                    <div className="text-sm font-medium text-gray-700">{ETICHETTA_FASE[fase]}</div>
                    <div
                        className="h-2 w-full bg-gray-100 rounded-full overflow-hidden"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(avanzamento * 100)}
                    >
                        <div
                            className="h-full bg-blue-600 transition-all duration-300"
                            style={{ width: `${Math.round(avanzamento * 100)}%` }}
                        />
                    </div>
                </div>
            )}

            {risultato && !inCorso && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-1" aria-live="polite">
                    <div className="font-bold text-gray-900">{testoRisultato(risultato)}</div>
                    <div className="text-sm text-gray-700">{testoStime(risultato.downloadMbps)}</div>
                </div>
            )}

            {errore && !inCorso && (
                <div className="bg-red-50 border border-red-100 text-red-700 rounded-lg p-3 text-sm">{errore}</div>
            )}

            <p className="text-xs text-gray-500 italic">
                Il risultato dipende dalla connessione di questo PC (rete, Wi-Fi, altri utilizzi in corso)
                verso il server dell'agenzia: da un altro PC o in un altro momento può essere diverso.
            </p>
        </div>
    );
};

export default ConnessioneTestPanel;
